// RITA-1505 (Nutzungserfassung Phase 2c, Epic RITA-1496) — Export der Nutzungsdaten.
//
// GET /usage/export?days=7|30|90 (middleware/usageEvents.ts, RITA-1503) liefert eine xlsx-Datei
// als Anhang. Der Aufruf ist bewusst RELATIV: /usage läuft lokal über setupProxy.js und in den
// Images über nginx auf dasselbe Backend; eine absolute Adresse wäre eine zweite Wahrheit und
// obendrein fremdherkünftig (Content-Disposition wäre dann nicht lesbar).
//
// Das Token steht wie überall in localStorage (context/CommunicationProvider/authUser); der
// Export geht nicht über Apollo, deshalb wird der Kopf hier selbst gesetzt. Genau deshalb
// erscheint der Export auch NICHT in der Eigenerfassung: erfasst sind nur Apollo und axios.
//
// Der Server schreibt je Aufruf einen Audit-Eintrag (usage_export) — ein Klick, eine Zeile.
import saveAsFile from 'file-saver';
import { getToken as tokenAusSpeicher } from '../../../context/CommunicationProvider/authUser';

export const USAGE_EXPORT_ENDPOINT = '/usage/export';

/**
 * Ersatzname ohne Zeitraumbehauptung. Der Server bildet den Namen aus dem TATSÄCHLICH
 * abgedeckten Fenster ('nutzung-2026-08-16-2026-09-14.xlsx'); ein selbst gebauter Name wie
 * 'nutzung-30-tage.xlsx' würde einen Zeitraum behaupten, den die Datei nicht enthält.
 */
export const USAGE_EXPORT_FALLBACK_NAME = 'nutzung.xlsx';

export type UsageExportError =
  | 'unauthorized'
  | 'forbidden'
  | 'invalid_days'
  | 'invalid_request'
  | 'export_failed'
  | 'unknown';

/** Fehlerklassen des Servers, jede mit eigener Meldung (kein „etwas ist schiefgelaufen“). */
const MELDUNGEN: Record<UsageExportError, string> = {
  unauthorized: 'Die Sitzung ist abgelaufen. Bitte melde dich neu an.',
  forbidden: 'Für den Export der Nutzungsdaten fehlt die Berechtigung.',
  invalid_days: 'Für diesen Zeitraum gibt es keinen Export. Erlaubt sind 7, 30 oder 90 Tage.',
  invalid_request: 'Die Anfrage wurde abgelehnt. Bitte wähle einen anderen Zeitraum.',
  export_failed: 'Der Export konnte nicht erstellt werden. Bitte später erneut versuchen.',
  unknown: 'Der Export konnte nicht geladen werden. Bitte später erneut versuchen.',
};

export interface UsageExportSuccess {
  ok: true;
  fileName: string;
}

export interface UsageExportFailure {
  ok: false;
  error: UsageExportError;
  message: string;
  /** true = Sitzung abgelaufen; der Aufrufer meldet ab und zeigt KEINE Fehlermeldung. */
  sessionExpired: boolean;
}

export type UsageExportResult = UsageExportSuccess | UsageExportFailure;

export type BlobFetchLike = (
  input: string,
  init?: RequestInit,
) => Promise<{
  ok: boolean;
  status: number;
  headers: { get: (name: string) => string | null };
  blob: () => Promise<Blob>;
  json: () => Promise<unknown>;
}>;

export type SaveFile = (data: Blob, fileName: string) => void;

interface Params {
  days: number;
  fetchImpl?: BlobFetchLike;
  getToken?: () => string | null;
  saveFile?: SaveFile;
}

const defaultFetch: BlobFetchLike = (input, init) => window.fetch(input, init);

const fehler = (error: UsageExportError): UsageExportFailure => ({
  ok: false,
  error,
  message: MELDUNGEN[error],
  sessionExpired: error === 'unauthorized',
});

const istFehlerschluessel = (wert: unknown): wert is UsageExportError =>
  typeof wert === 'string' && wert in MELDUNGEN && wert !== 'unknown';

/**
 * Dateiname aus Content-Disposition. Akzeptiert wird nur ein einfacher xlsx-Name ohne Pfadanteil;
 * alles andere (fehlender Kopf, Pfadangabe, andere Endung) fällt auf den Ersatznamen zurück.
 */
export const fileNameFromDisposition = (disposition: string | null): string => {
  if (!disposition) return USAGE_EXPORT_FALLBACK_NAME;
  const kodiert = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const einfach = /filename="?([^";]+)"?/i.exec(disposition);
  let name = '';
  try {
    name = kodiert ? decodeURIComponent(kodiert[1].trim()) : (einfach?.[1] ?? '').trim();
  } catch {
    return USAGE_EXPORT_FALLBACK_NAME;
  }
  return /^[A-Za-z0-9._-]+\.xlsx$/.test(name) ? name : USAGE_EXPORT_FALLBACK_NAME;
};

/**
 * Lädt den Export und übergibt ihn dem Speichern-Dialog des Browsers. Wirft nie: jeder Ausgang
 * ist ein Ergebnis mit Meldung, damit ein Klick nicht in einer unbehandelten Ablehnung endet
 * (die Fehlererfassung hört auf 'unhandledrejection' und würde sie als Anwendungsfehler melden).
 */
export const downloadUsageExport = async ({
  days,
  fetchImpl = defaultFetch,
  getToken = tokenAusSpeicher,
  saveFile = saveAsFile,
}: Params): Promise<UsageExportResult> => {
  const token = getToken();
  if (!token) return fehler('unauthorized');
  try {
    const antwort = await fetchImpl(`${USAGE_EXPORT_ENDPOINT}?days=${days}`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!antwort.ok) {
      const rumpf = await antwort.json().catch(() => null);
      const schluessel = (rumpf as { error?: unknown } | null)?.error;
      return fehler(istFehlerschluessel(schluessel) ? schluessel : 'unknown');
    }
    const datei = await antwort.blob();
    const fileName = fileNameFromDisposition(antwort.headers.get('Content-Disposition'));
    saveFile(datei, fileName);
    return { ok: true, fileName };
  } catch {
    return fehler('unknown');
  }
};

export default downloadUsageExport;
